import React, { useMemo, useState } from 'react'
import { Box, Button, Link, Typography } from '@mui/material'
import Linkify from 'react-linkify'
import useCheckMessageEditable from 'src/feed/EditChatMessage/useCheckMessageEditable'
import CoachName from 'src/legacy/components/CoachName/CoachName'
import { SmsMessageStatus } from 'src/graphql.generated'
import { formatTime } from '../../format'
import { advancedColors, surfaces } from '../../theme'
import ForwardedItemCard from './ForwardedItemCard'
import LinkedContentCard from './LinkedContentCard'
import MessageAttachments from './attachments/MessageAttachments'
import { ChatBubbleEntry, ChatMessageBubbleEntry } from './types'

const nameFor = (entry: ChatMessageBubbleEntry): React.ReactNode => {
  if (entry.isMine) {
    return 'You'
  }
  if (entry.authorName !== undefined) {
    return entry.authorName
  }
  return entry.authorCoachId ? <CoachName coachID={entry.authorCoachId} /> : 'Patient'
}

const openInNewTab = (href: string, text: string, key: number) => (
  <Link key={key} href={href} target="_blank" rel="noreferrer" color="inherit" underline="always">
    {text}
  </Link>
)

const SMS_STATUS_LABELS: Partial<Record<SmsMessageStatus, string>> = {
  [SmsMessageStatus.Failed]: 'Failed to send',
  [SmsMessageStatus.Undelivered]: 'Failed to send',
  [SmsMessageStatus.Accepted]: 'Sending…',
  [SmsMessageStatus.Queued]: 'Sending…',
  [SmsMessageStatus.Sending]: 'Sending…',
  [SmsMessageStatus.Scheduled]: 'Sending…',
  [SmsMessageStatus.Sent]: 'Sent',
}

// In the Feed the row shows the time, and a message is named only when another coach wrote it.
type Props = { entry: ChatBubbleEntry; continued?: boolean; inFeed?: boolean; careTeam?: boolean }

const ChatMessageBubble = ({ entry, continued = false, inFeed = false, careTeam = false }: Props) => {
  const [editable, setEditable] = useState(false)
  const editableFrom = entry.variant !== 'activity' && entry.onEdit ? entry.createdAt : null
  const sentAt = useMemo(() => (editableFrom ? new Date(editableFrom) : undefined), [editableFrom])
  useCheckMessageEditable(sentAt, setEditable)

  if (entry.variant === 'activity') {
    return (
      <Box sx={{ textAlign: 'center', py: 0.5, width: '100%' }}>
        <Typography variant="caption" sx={{ color: advancedColors.inkFaint }}>
          {entry.text} · {formatTime(entry.createdAt)}
        </Typography>
      </Box>
    )
  }

  const status = entry.smsStatus ? SMS_STATUS_LABELS[entry.smsStatus] : undefined
  const byOtherCoach = !entry.isMine && !!entry.authorCoachId
  const showsName = !inFeed || byOtherCoach
  const smsTag = entry.channel === 'sms' ? 'SMS' : null
  const timeTag = inFeed ? smsTag : [smsTag, formatTime(entry.createdAt)].filter(Boolean).join(' · ')
  const showsHeader = !continued && (showsName || !!timeTag)
  const attachments = entry.attachments ?? []
  const onEdit = editable ? entry.onEdit : undefined
  const hasBody = !!entry.text || !!entry.forwardedItem || !!entry.linkedTo || attachments.length > 0
  const incomingBackground = careTeam ? surfaces.blue.bg : advancedColors.green

  return (
    <Box
      sx={{
        maxWidth: '80%',
        width: 'fit-content',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      {showsHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.75,
            justifyContent: entry.isMine ? 'flex-end' : 'flex-start',
          }}
        >
          {showsName && (
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: advancedColors.inkMuted, overflowWrap: 'anywhere', minWidth: 0 }}
            >
              {nameFor(entry)}
            </Typography>
          )}
          {!entry.isMine && entry.authorRole && (
            <Typography variant="caption" sx={{ color: advancedColors.inkFaint }}>
              {entry.authorRole}
            </Typography>
          )}
          {timeTag && (
            <Typography variant="caption" sx={{ color: advancedColors.inkFaint }}>
              {timeTag}
            </Typography>
          )}
        </Box>
      )}
      {hasBody && (
        <Box
          title={continued ? formatTime(entry.createdAt) : undefined}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignSelf: entry.isMine ? 'flex-end' : 'flex-start',
            width: 'fit-content',
            maxWidth: '100%',
            minWidth: 0,
            px: 2,
            py: 1.25,
            borderRadius: entry.isMine ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
            backgroundColor: entry.isMine ? surfaces.blue.fg : incomingBackground,
            color: careTeam && !entry.isMine ? surfaces.blue.fg : advancedColors.surface,
          }}
        >
          {entry.linkedTo && (
            <Box sx={{ m: -1, mb: 0 }}>
              <LinkedContentCard link={entry.linkedTo} />
            </Box>
          )}
          {entry.text && (
            <Typography variant="body2" sx={{ overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
              <Linkify componentDecorator={openInNewTab}>{entry.text}</Linkify>
            </Typography>
          )}
          {attachments.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
              <MessageAttachments attachments={attachments} />
            </Box>
          )}
          {entry.forwardedItem && <ForwardedItemCard item={entry.forwardedItem} />}
        </Box>
      )}
      {(status || onEdit) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: entry.isMine ? 'flex-end' : 'flex-start',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {status && (
            <Typography variant="caption" sx={{ color: advancedColors.inkFaint }}>
              {status}
            </Typography>
          )}
          {onEdit && (
            <Button size="small" onClick={onEdit} aria-label="Edit message">
              Edit
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}

export default ChatMessageBubble
